<div class="rf-page">
  <div class="rf-save-bar">
    <div class="rf-save-bar__inner">
      <div class="rf-save-bar__left">
        <button
          type="button"
          class="rf-save-bar__back"
          [attr.aria-label]="'requirements.form.backAriaLabel' | translate"
          (click)="onCancel()"
        >
          <mat-icon fontSet="material-icons-outlined" aria-hidden="true">chevron_left</mat-icon>
        </button>
        <div class="rf-save-bar__heading">
          @if (isEdit) {
            <span class="rf-save-bar__eyebrow">
              @if (requirement()?.extId) {
                {{ requirement()!.extId }} &middot;
              }
              {{ 'requirements.form.entityLabel' | translate }}
            </span>
            <span class="rf-save-bar__title">{{ 'requirements.form.editTitle' | translate }}</span>
          } @else {
            <span class="rf-save-bar__eyebrow">{{ 'requirements.form.newLabel' | translate }}</span>
            <span class="rf-save-bar__title">{{ 'requirements.form.createTitle' | translate }}</span>
          }
        </div>
        @if (isDirty()) {
          <span class="rf-dirty-pill">
            <span class="rf-dirty-pill__dot" aria-hidden="true"></span>
            {{ 'requirements.form.unsavedChanges' | translate }}
          </span>
        }
        @if (!isReadOnly() && saveBlockedHintKey(); as hintKey) {
          <span id="req-save-blocked-hint" class="rf-save-blocked" role="status" data-testid="req-save-blocked-hint">
            <mat-icon class="rf-save-blocked__icon" aria-hidden="true">error_outline</mat-icon>
            {{ hintKey | translate }}
          </span>
        }
      </div>
      <div class="rf-save-bar__right">
        <button mat-stroked-button type="button" (click)="onCancel()">
          {{ 'requirements.form.cancel' | translate }}
        </button>
        @if (!isReadOnly()) {
          <button
            mat-flat-button
            color="primary"
            type="button"
            data-testid="req-save-btn"
            [disabled]="saveDisabled()"
            [attr.aria-describedby]="saveBlockedHintKey() ? 'req-save-blocked-hint' : null"
            (click)="onSave()"
          >
            <mat-icon aria-hidden="true">check</mat-icon>
            {{ (isEdit ? 'requirements.form.save' : 'requirements.form.saveCreate') | translate }}
          </button>
        }
      </div>
    </div>
  </div>

  @if (isReadOnly()) {
    <div class="rf-readonly-hint-wrap">
      <div class="rf-readonly-hint">
        <mat-icon class="rf-readonly-hint__icon" aria-hidden="true">info</mat-icon>
        <p class="rf-readonly-hint__text">{{ 'requirements.form.readOnlyHint' | translate }}</p>
      </div>
    </div>
  }

  @if (loading()) {
    <div class="rf-page__loading">
      <mat-spinner diameter="32"></mat-spinner>
    </div>
  } @else if (loadError()) {
    <p class="rf-page__error">{{ 'requirements.form.loadError' | translate }}</p>
  } @else {
    <form [formGroup]="form" class="rf-body">
      <div class="rf-main">
        <!-- Jira Card -->
        <div class="rf-card">
          @if (hasLinkedIssue()) {
            <div class="rf-jira-linked">
              <app-provider-badge
                data-testid="requirement-linked-provider"
                [providerType]="linkedProviderType()"
                [label]="linkedProviderName()"
              ></app-provider-badge>
              <div class="rf-jira-linked__content">
                <div class="rf-jira-linked__label">
                  {{ 'requirements.form.linkedIssueLabel' | translate: { provider: linkedProviderName() } }}
                  <span class="rf-jira-linked__badge">{{ 'requirements.form.jiraSyncStatus' | translate }}</span>
                </div>
                <div class="rf-jira-linked__key-row">
                  <a
                    class="rf-jira-linked__key"
                    [href]="linkedBrowseUrl()"
                    target="_blank"
                    rel="noopener noreferrer"
                    [attr.aria-label]="'requirements.jira.openTicketAria' | translate: { key: linkedIssueKey() }"
                  >
                    {{ linkedIssueKey() }}
                    <mat-icon class="rf-jira-linked__key-icon" aria-hidden="true">chevron_right</mat-icon>
                  </a>
                  @if (linkedTitleSnapshot()) {
                    <span class="rf-jira-linked__summary">{{ linkedTitleSnapshot() }}</span>
                  }
                </div>
                @if (linkedSyncedAt()) {
                  <div class="rf-jira-linked__synced">
                    {{ 'requirements.jira.syncedAt' | translate }}:
                    {{ linkedSyncedAt() | date: 'dd.MM.yyyy, HH:mm' }}
                  </div>
                }
                @if (linkedIntegration()) {
                  <app-linked-issue
                    class="rf-jira-linked__live"
                    [showKey]="false"
                    [providerType]="linkedProviderType()"
                    [issueKey]="linkedIssueKey()!"
                    [projectId]="projectId"
                    [integrationId]="linkedIntegration()!.id"
                    [boardUrl]="linkedIntegration()!.boardUrl"
                  ></app-linked-issue>
                }
              </div>
              @if (!isReadOnly()) {
                <div class="rf-jira-linked__actions">
                  <button
                    type="button"
                    class="rf-jira-linked__refresh"
                    [disabled]="refreshing() || jiraPreviewBusy()"
                    (click)="onRefreshJira()"
                  >
                    <mat-icon aria-hidden="true">refresh</mat-icon>
                    {{ 'requirements.jira.refresh' | translate }}
                  </button>
                  <button
                    type="button"
                    class="rf-jira-linked__unlink"
                    [attr.aria-label]="'requirements.jira.unlinkAria' | translate: { key: linkedIssueKey() }"
                    (click)="onUnlinkClick()"
                  >
                    <mat-icon aria-hidden="true">link_off</mat-icon>
                    {{ 'requirements.jira.unlink' | translate }}
                  </button>
                </div>
              }
            </div>
            <app-confirm-dialog
              [visible]="showUnlinkConfirm()"
              icon="link_off"
              [title]="'requirements.jira.unlinkConfirmTitle' | translate"
              [message]="'requirements.jira.unlinkConfirmMessage' | translate: { key: linkedIssueKey() }"
              [confirmLabel]="'requirements.jira.unlinkConfirm' | translate"
              [cancelLabel]="'requirements.jira.unlinkCancel' | translate"
              (confirmed)="onConfirmUnlink()"
              (cancelled)="onCancelUnlink()"
            ></app-confirm-dialog>
            @if (refreshError()) {
              <p class="rf-card__error">{{ 'requirements.jira.refreshError' | translate }}</p>
            }
            @if (jiraPreviewError()) {
              <p class="rf-card__error">{{ 'requirements.jira.previewError' | translate }}</p>
            }
          } @else if (!isEdit) {
            <div class="rf-jira-connect">
              <div class="rf-jira-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 15l6-6M10 6l1-1a4 4 0 015.7 5.7l-1 1M14 18l-1 1a4 4 0 01-5.7-5.7l1-1"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
              <div class="rf-jira-connect__text">
                <div class="rf-jira-connect__title">{{ 'requirements.form.jiraConnectTitle' | translate }}</div>
                <div class="rf-jira-connect__subtitle">{{ 'requirements.form.jiraConnectSubtitle' | translate }}</div>
              </div>
              @if (!isReadOnly()) {
                <span
                  class="rf-jira-btn-wrap"
                  [matTooltip]="'requirements.form.jiraConnectButtonDisabledTooltip' | translate"
                  [matTooltipDisabled]="hasActiveJiraIntegration()"
                >
                  <button
                    mat-stroked-button
                    class="rf-jira-btn"
                    type="button"
                    data-testid="requirement-link-button"
                    [disabled]="!hasActiveJiraIntegration()"
                    [attr.aria-label]="
                      hasActiveJiraIntegration()
                        ? null
                        : ('requirements.form.jiraConnectButtonDisabledTooltip' | translate)
                    "
                    (click)="onJiraConnectClick()"
                  >
                    {{ 'requirements.form.jiraConnectButton' | translate }}
                  </button>
                </span>
              }
            </div>
          } @else {
            <div class="rf-jira-connect">
              <div class="rf-jira-connect__icon rf-jira-connect__icon--muted">
                <mat-icon aria-hidden="true">link_off</mat-icon>
              </div>
              <div class="rf-jira-connect__text">
                <div class="rf-jira-connect__title">{{ 'requirements.jira.notLinked' | translate }}</div>
              </div>
            </div>
          }
          @if (!isEdit) {
            <app-issue-picker-modal
              [visible]="showJiraPicker()"
              [projectId]="projectId"
              (visibleChange)="showJiraPicker.set($event)"
              (issueSelected)="onJiraIssueSelected($event)"
            ></app-issue-picker-modal>
          }
        </div>

        <!-- Grunddaten Card -->
        <div class="rf-card">
          <div class="rf-card__body">
            <div class="rf-section-title">
              <div>
                <div class="rf-section-title__title">{{ 'requirements.form.grunddatenTitle' | translate }}</div>
                <div class="rf-section-title__subtitle">{{ 'requirements.form.grunddatenSubtitle' | translate }}</div>
              </div>
            </div>
            <mat-form-field appearance="outline" class="rf-field">
              <mat-label>{{ 'requirements.form.title' | translate }}</mat-label>
              <input matInput formControlName="title" [attr.maxlength]="fieldLengths.NAME_MAX" />
              @if (form.controls.title.hasError('required')) {
                <mat-error>{{ 'requirements.form.titleRequired' | translate }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline" class="rf-field">
              <textarea
                matInput
                formControlName="description"
                rows="5"
                [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                [placeholder]="'requirements.form.description' | translate"
              ></textarea>
              <mat-hint align="end" aria-live="polite">
                {{ form.controls.description.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
              </mat-hint>
            </mat-form-field>
            @if (isEdit && requirement()) {
              <div class="rf-id-row">
                <span class="rf-id-row__label">{{ 'requirements.form.id' | translate }}</span>
                <code class="rf-id-row__code">{{ requirement()!.id }}</code>
                <button
                  mat-icon-button
                  type="button"
                  class="rf-id-row__copy"
                  [attr.aria-label]="'requirements.form.copyIdAriaLabel' | translate"
                  (click)="copyId()"
                >
                  <mat-icon aria-hidden="true">content_copy</mat-icon>
                </button>
              </div>
            }
          </div>
        </div>

        <!-- Akzeptanzkriterien Card -->
        <div class="rf-card">
          <div class="rf-ac-header">
            <div class="rf-ac-header__left">
              <div class="rf-ac-header__title">{{ 'requirements.acceptanceCriteria.title' | translate }}</div>
              @if (!criteriaLoadError()) {
                <div class="rf-ac-header__count">
                  {{ acCheckedCount() }}
                  {{ 'requirements.acceptanceCriteria.progressOf' | translate: { total: acDrafts().length } }}
                </div>
              }
            </div>
            @if (!criteriaLoadError()) {
              <div class="rf-ac-progress">
                <div class="rf-ac-progress__bar" [style.width]="acProgressPercent() + '%'"></div>
              </div>
            }
          </div>
          @if (criteriaLoadError()) {
            <div class="rf-load-error rf-load-error--padded" role="alert" data-testid="criteria-load-error">
              <mat-icon class="rf-load-error__icon" aria-hidden="true">error_outline</mat-icon>
              <p class="rf-load-error__text">{{ 'requirements.form.criteriaLoadError' | translate }}</p>
              <button
                mat-stroked-button
                type="button"
                data-testid="criteria-retry"
                [attr.aria-label]="'requirements.form.retryLoadCriteria' | translate"
                [disabled]="retrying()"
                (click)="onRetryChildLists()"
              >
                <mat-icon aria-hidden="true">refresh</mat-icon>
                {{ 'requirements.form.retryLoad' | translate }}
              </button>
            </div>
          } @else {
            <app-acceptance-criteria-table
              [criteria]="acDrafts()"
              [readOnly]="isReadOnly()"
              [contentLocked]="jiraLocked()"
              (changed)="onAcChanged($event)"
            ></app-acceptance-criteria-table>
          }
        </div>

        <!-- Testfälle Card -->
        <div class="rf-card">
          <div class="rf-card__body">
            <div class="rf-section-title">
              <div>
                <div class="rf-section-title__title">{{ 'requirements.testCases.title' | translate }}</div>
                <div class="rf-section-title__subtitle">{{ 'requirements.testCases.subtitle' | translate }}</div>
              </div>
              <app-ki-anforderung-tools
                [projectId]="projectId"
                [requirementId]="requirementId"
                [anfId]="kiAnfId()"
                [anfTitel]="requirement()?.title ?? ''"
                [verknuepfungenUngespeichert]="verknuepfungenUngespeichert()"
                [speichernFuerPruefung]="speichernFuerPruefung"
                [darfVerknuepfen]="!isReadOnly()"
                (testfallUebernommen)="onKiTestfallUebernommen($event)"
                (abdeckungVerknuepft)="onKiAbdeckungVerknuepft($event)"
                (verknuepfungenGeaendert)="onKiVerknuepfungenGeaendert()"
              />
            </div>
            @if (coverageLoadError()) {
              <div class="rf-load-error" role="alert" data-testid="coverage-load-error">
                <mat-icon class="rf-load-error__icon" aria-hidden="true">error_outline</mat-icon>
                <p class="rf-load-error__text">{{ 'requirements.form.coverageLoadError' | translate }}</p>
                <button
                  mat-stroked-button
                  type="button"
                  data-testid="coverage-retry"
                  [attr.aria-label]="'requirements.form.retryLoadCoverage' | translate"
                  [disabled]="retrying()"
                  (click)="onRetryChildLists()"
                >
                  <mat-icon aria-hidden="true">refresh</mat-icon>
                  {{ 'requirements.form.retryLoad' | translate }}
                </button>
              </div>
            } @else if (linkedTestCasesDisplay().length > 0) {
              <div class="rf-tc-list">
                @for (item of linkedTestCasesDisplay(); track item.coverage.testCaseId) {
                  <div
                    class="rf-tc-list__item rf-tc-list__item--link"
                    role="link"
                    tabindex="0"
                    [attr.aria-label]="'requirements.testCases.openAriaLabel' | translate"
                    (click)="onJumpToLinkedTestCase(item.coverage.testCaseId)"
                    (keydown.enter)="onJumpToLinkedTestCase(item.coverage.testCaseId)"
                  >
                    @if (item.testCase?.key) {
                      <code class="rf-tc-list__id">{{ item.testCase!.key }}</code>
                    }
                    <span class="rf-tc-list__title">{{ item.testCase?.title ?? item.coverage.testCaseId }}</span>
                    @if (item.testCase?.kiEntwurfAm; as kiEntwurfAm) {
                      <app-ki-badge
                        [label]="
                          'requirements.testCases.ausKiEntwurf'
                            | translate: { datum: (kiEntwurfAm | date: 'dd.MM.yyyy') }
                        "
                      />
                    }
                    @if (item.testCase?.testType; as testType) {
                      <span class="rf-tc-list__testart">{{ 'testCases.testType.' + testType | translate }}</span>
                    }
                    @if (item.testCase?.status; as status) {
                      <span [class]="'rf-tc-list__status tc-pill tc-pill--status-' + status.toLowerCase()">
                        {{ 'testCases.status.' + status | translate }}
                      </span>
                    }
                    @if (!isReadOnly()) {
                      <button
                        mat-icon-button
                        type="button"
                        class="rf-tc-list__unlink"
                        data-testid="rf-tc-unlink"
                        [attr.aria-label]="'requirements.testCases.unlinkAriaLabel' | translate"
                        (click)="$event.stopPropagation(); unlinkTestCase(item.coverage.testCaseId)"
                      >
                        <mat-icon aria-hidden="true">close</mat-icon>
                      </button>
                    }
                    <mat-icon class="rf-tc-list__chevron" aria-hidden="true">chevron_right</mat-icon>
                  </div>
                }
              </div>
              @if (!isReadOnly()) {
                <div class="rf-tc-actions">
                  <button
                    mat-flat-button
                    color="primary"
                    type="button"
                    data-testid="req-create-testcase-btn"
                    (click)="onJumpToTestCase()"
                  >
                    <mat-icon aria-hidden="true">arrow_forward</mat-icon>
                    {{ 'requirements.form.testfaelleCreate' | translate }}
                  </button>
                  <button
                    mat-stroked-button
                    type="button"
                    [disabled]="!availableTcs().length"
                    (click)="onVerknuepfenClick()"
                  >
                    <mat-icon aria-hidden="true">grid_view</mat-icon>
                    {{ 'requirements.testCases.linkTestCase' | translate }}
                  </button>
                </div>
              }
            } @else {
              <div class="rf-empty-state">
                <div class="rf-empty-state__icon-box" aria-hidden="true">
                  <mat-icon>check</mat-icon>
                </div>
                <p class="rf-empty-state__title">{{ 'requirements.form.testfaelleEmptyTitle' | translate }}</p>
                <p class="rf-empty-state__subtitle">{{ 'requirements.form.testfaelleEmptySubtitle' | translate }}</p>
                @if (!isReadOnly()) {
                  <div class="rf-empty-state__actions">
                    <button
                      mat-flat-button
                      color="primary"
                      type="button"
                      data-testid="req-create-testcase-btn"
                      (click)="onJumpToTestCase()"
                    >
                      <mat-icon aria-hidden="true">arrow_forward</mat-icon>
                      {{ 'requirements.form.testfaelleCreate' | translate }}
                    </button>
                    <button
                      mat-stroked-button
                      type="button"
                      [disabled]="!availableTcs().length"
                      (click)="onVerknuepfenClick()"
                    >
                      <mat-icon aria-hidden="true">grid_view</mat-icon>
                      {{ 'requirements.testCases.linkTestCase' | translate }}
                    </button>
                  </div>
                }
              </div>
            }
            <app-tc-picker-modal
              [testCases]="availableTcs()"
              [projectId]="projectId"
              [busy]="false"
              [visible]="showTcModal()"
              (visibleChange)="showTcModal.set($event)"
              (confirm)="addLinkedTestCases($event)"
            ></app-tc-picker-modal>
          </div>
        </div>

        <!-- Notiz Card -->
        <div class="rf-card">
          <div class="rf-card__body">
            <div class="rf-section-title">
              <div>
                <div class="rf-section-title__title">{{ 'requirements.form.notizTitle' | translate }}</div>
                <div class="rf-section-title__subtitle">{{ 'requirements.form.notizSubtitle' | translate }}</div>
              </div>
            </div>
            <mat-form-field appearance="outline" floatLabel="always" class="rf-field">
              <mat-label>{{ 'requirements.details.notes' | translate }}</mat-label>
              <textarea
                matInput
                formControlName="notes"
                rows="3"
                [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                [placeholder]="'requirements.form.notesPlaceholder' | translate"
              ></textarea>
              <mat-hint align="end" aria-live="polite">
                {{ form.controls.notes.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
              </mat-hint>
            </mat-form-field>
          </div>
        </div>
      </div>

      <div class="rf-sidebar">
        <!-- Eigenschaften Card -->
        <div class="rf-card">
          <div class="rf-card__body rf-card__body--sidebar">
            <div class="rf-card__inline-header">{{ 'requirements.form.eigenschaften' | translate }}</div>
            <mat-form-field appearance="outline" floatLabel="always" class="rf-field">
              <mat-label>{{ 'requirements.form.type' | translate }}</mat-label>
              <mat-select formControlName="type" data-testid="requirement-type-select">
                <mat-select-trigger>{{ form.controls.type.value | requirementTypeLabel }}</mat-select-trigger>
                @for (opt of effectiveTypeOptions(); track opt.id) {
                  <mat-option [value]="opt.value">{{ opt.value | requirementTypeLabel }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            @if (form.controls.type.value === subTaskTypeValue) {
              <mat-form-field appearance="outline" floatLabel="always" class="rf-field">
                <mat-label>{{ 'requirements.form.parentStory' | translate }}</mat-label>
                <mat-select formControlName="parentRequirementId">
                  <mat-option [value]="''">{{ 'requirements.form.noParentStory' | translate }}</mat-option>
                  @for (story of parentStories(); track story.id) {
                    <mat-option [value]="story.id">
                      @if (story.extId) {
                        {{ story.extId }} ·
                      }
                      {{ story.title }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }
            <mat-form-field appearance="outline" floatLabel="always" class="rf-field">
              <mat-label>{{ 'requirements.form.priority' | translate }}</mat-label>
              <mat-select formControlName="priority">
                @for (priority of priorityOptions; track priority) {
                  <mat-option [value]="priority">{{ 'requirements.priority.' + priority | translate }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <div class="rf-priority-preview" aria-hidden="true">
              <span [class]="'rf-prio-badge rf-prio-badge--' + priorityBadgeModifier()">
                {{ 'requirements.priority.' + priorityValue() | translate }}
              </span>
            </div>
            <mat-form-field appearance="outline" floatLabel="always" class="rf-field">
              <mat-label>{{ 'requirements.form.status' | translate }}</mat-label>
              <mat-select formControlName="status">
                <mat-select-trigger>{{ form.controls.status.value | requirementStatusLabel }}</mat-select-trigger>
                @for (opt of effectiveStatusOptions(); track opt.value) {
                  <mat-option [value]="opt.value">{{ opt.value | requirementStatusLabel }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            @if (!isReadOnly()) {
              <app-requirement-status-editor
                [entries]="statusEditorEntries()"
                [open]="statusEditorOpen()"
                [disabled]="saving()"
                (toggleClicked)="onStatusEditorToggle()"
                (entriesChange)="onStatusEditorChange($event)"
                (deleteRequested)="onStatusEditorDeleteRequested($event)"
                (reorderPersisted)="onStatusEditorReorderPersisted($event)"
              />
            }
            <app-person-select
              formControlName="ownerId"
              [label]="'requirements.form.owner' | translate"
              [options]="ownerOptions()"
              [allowNone]="false"
              [required]="true"
            />
            @if (form.controls.ownerId.hasError('required') && form.controls.ownerId.touched) {
              <p class="rf-field-error" role="alert">{{ 'requirements.form.ownerRequired' | translate }}</p>
            }
            <app-person-multi-select
              formControlName="reviewerIds"
              [label]="'requirements.form.reviewers' | translate"
              [options]="ownerOptions()"
              placeholderKey="requirements.form.addReviewer"
              removeKey="requirements.form.removeReviewer"
            />
            <mat-form-field appearance="outline" class="rf-field">
              <input
                matInput
                formControlName="version"
                [attr.maxlength]="fieldLengths.KEY_MAX"
                [placeholder]="'requirements.form.version' | translate"
              />
            </mat-form-field>
            <mat-form-field appearance="outline" class="rf-field">
              <mat-chip-grid #tagGrid [attr.aria-label]="'requirements.form.tagsAriaLabel' | translate">
                @for (tag of form.controls.tags.value; track tag) {
                  <mat-chip-row [removable]="!tagsLocked()" (removed)="removeTag(tag)">
                    {{ tag }}
                    @if (!tagsLocked()) {
                      <button matChipRemove type="button" [attr.aria-label]="'requirements.form.removeTag' | translate">
                        <mat-icon aria-hidden="true">cancel</mat-icon>
                      </button>
                    }
                  </mat-chip-row>
                }
              </mat-chip-grid>
              <input
                [matChipInputFor]="tagGrid"
                [disabled]="tagsLocked()"
                [placeholder]="tagsLocked() ? '' : ('requirements.form.tagPlaceholder' | translate)"
                (matChipInputTokenEnd)="addTag($event)"
              />
            </mat-form-field>
          </div>
        </div>

        <!-- Automatisierung Card -->
        <app-requirement-automation-card
          [statusControl]="form.controls.automationStatus"
          [frameworkControl]="form.controls.testFramework"
          [automationKey]="requirement()?.automationKey ?? null"
          [readOnly]="isReadOnly()"
        ></app-requirement-automation-card>

        <!-- Details & Historie Card (edit only) -->
        @if (isEdit && requirement()) {
          <div class="rf-card">
            <div class="rf-card__body">
              <div class="rf-card__inline-header">{{ 'requirements.details.title' | translate }}</div>
              <div class="rf-meta-row">
                <span class="rf-meta-row__label">{{ 'requirements.details.author' | translate }}</span>
                <span class="rf-meta-row__value">{{ requirement()!.authorUsername || '—' }}</span>
              </div>
              <div class="rf-meta-row">
                <span class="rf-meta-row__label">{{ 'requirements.details.created' | translate }}</span>
                <span class="rf-meta-row__value">{{ requirement()!.createdAt | date: 'dd.MM.yyyy, HH:mm' }}</span>
              </div>
              <div class="rf-meta-row">
                <span class="rf-meta-row__label">{{ 'requirements.details.updated' | translate }}</span>
                <span class="rf-meta-row__value">{{ requirement()!.updatedAt | date: 'dd.MM.yyyy, HH:mm' }}</span>
              </div>
              <div class="rf-meta-row">
                <span class="rf-meta-row__label">{{ 'requirements.details.updatedBy' | translate }}</span>
                <span class="rf-meta-row__value">{{ requirement()!.updatedByUsername || '—' }}</span>
              </div>
              <button type="button" class="rf-history-btn" (click)="openHistory()">
                <mat-icon aria-hidden="true">history</mat-icon>
                {{ 'requirements.form.historyButton' | translate }}
              </button>
            </div>
          </div>
        }
      </div>
    </form>

    @if (saveUnavailable()) {
      <div class="rf-load-error rf-load-error--save" role="alert" data-testid="error-unavailable">
        <mat-icon class="rf-load-error__icon" aria-hidden="true">cloud_off</mat-icon>
        <p class="rf-load-error__text">{{ 'requirements.form.saveUnavailable' | translate }}</p>
        <button mat-stroked-button type="button" data-testid="retry-load" [disabled]="saving()" (click)="onSave()">
          <mat-icon aria-hidden="true">refresh</mat-icon>
          {{ 'common.retry' | translate }}
        </button>
      </div>
    } @else if (saveError()) {
      <p class="rf-page__error rf-page__error--save">{{ 'requirements.form.saveError' | translate }}</p>
    }
  }
</div>

<lib-modal
  [title]="'common.unsavedChanges.title' | translate"
  [text]="'requirements.form.unsavedMessage' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'common.unsavedChanges.leave' | translate"
  [cancelText]="'common.unsavedChanges.stay' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
